import {
  KuxFilteredCategoriesDTO,
  AssetStructEnum,
  getWidthByHeight,
  getRatioByType,
  KuxDms,
  MediaType,
  AdditionalMediaType,
} from '@kaltura-ott/tvpil';
import { action } from 'mobx';

import { errorHandling } from '../../../utils/error-handling';

export interface AssetDetailsPage {
  assetTypeId: number;
  assetType: string;
  properties: KuxFilteredCategoriesDTO['assetDetailsPageProperty'];
  uiDesignElements: KuxFilteredCategoriesDTO['uiDesignElements'];
  tree: KuxFilteredCategoriesDTO;
}

/**
 * Details page store for asset
 * @category Store
 */
export class AssetDetailsPageStore {
  static moduleName = 'AssetDetailsPageStore';

  private list: AssetDetailsPage[];

  constructor(assetDetailsPageTrees: KuxFilteredCategoriesDTO[], rootTree: KuxFilteredCategoriesDTO) {
    this.list = assetDetailsPageTrees.map((tree) => {
      let ratio;
      if (tree.assetDetailsPageProperty.assetPresentationImageType) {
        ratio = getRatioByType(tree.assetDetailsPageProperty.assetPresentationImageType);
      }

      return {
        assetTypeId: -1,
        assetType: tree.assetDetailsPageProperty.struct || AssetStructEnum.default,
        properties: {
          ...tree.assetDetailsPageProperty,
          externalPortalUrl: rootTree.assetDetailsPageProperty.externalPortalUrl,
          imageWidth: Math.ceil(getWidthByHeight(tree.assetDetailsPageProperty.imageHeight, ratio)),
          presentationRatio: ratio || tree.assetDetailsPageProperty.presentationRatio,
        },
        uiDesignElements: tree.uiDesignElements,
        tree,
      };
    });
  }

  @action
  @errorHandling()
  init(dms: KuxDms) {
    this.list = this.list.map((details) => ({
      ...details,
      // @ts-expect-error TODO Fix
      assetTypeId: Number(dms.mediaTypes[details.assetType] || -1),
    }));
  }

  getDefault() {
    const defaults = this.list.find(
      (details) => details.assetType.toLowerCase() === AssetStructEnum.default.toLowerCase(),
    );
    return defaults as AssetDetailsPage;
  }

  getByType(type: MediaType | AdditionalMediaType) {
    return this.list.find((details) => details.assetType?.toLowerCase() === type.toLowerCase()) || this.getDefault();
  }

  getById(id: number): AssetDetailsPage {
    return this.list.find((details) => details.assetTypeId === id) || this.getDefault();
  }

  getImageProperties(type: MediaType | AdditionalMediaType) {
    const properties = this.getByType(type).properties;

    if (!properties) {
      return undefined;
    }

    const imageType = properties.assetPresentationImageType;

    if (!imageType) {
      return;
    }

    const imageRatio = getRatioByType(imageType);

    if (!imageRatio) {
      return;
    }

    return {
      imageType,
      imageRatio,
      imageHeight: properties.imageHeight,
    };
  }
}
